import { z } from "zod";
import { PublicIdSchema } from "./common";

// ===== 取引先別 請求書 Excel 様式設定 (F25 / Issue #267) =====
//
// 受領した現行請求書 5 社分の解析に基づく「キャリーカンパニー標準骨格」を
// 取引先ごとの JSON 設定で出し分ける。clients.invoice_excel_config に保存し、
// null の取引先は既定様式 (明細形式設定 invoice_line_mode に応じた明細型/集計型) で出力する。

/**
 * 明細型 (detailed) の先頭列レジストリ。key は invoice_items.excel_detail の
 * フィールド名と一致する。末尾の 税率 / 運賃(税抜) / 立寄・待機料(税抜) /
 * 高速・駐車場代等(内税) は様式固定のためレジストリに含めない。
 */
export const INVOICE_EXCEL_COLUMNS = {
  work_date: "日付",
  order_number: "受注番号",
  pickup_name: "発送元",
  pickup_address: "発地",
  area: "地区",
  delivery_name: "配送先",
  delivery_address: "配送先住所",
  cargo_name: "品名",
  cargo_quantity: "個数",
  distance_km: "距離",
  remarks: "備考",
} as const;

export const INVOICE_EXCEL_COLUMN_KEYS = Object.keys(
  INVOICE_EXCEL_COLUMNS,
) as readonly (keyof typeof INVOICE_EXCEL_COLUMNS)[];

export const InvoiceExcelColumnKey = z.enum(INVOICE_EXCEL_COLUMN_KEYS as [string, ...string[]]);

/** columns 未指定の明細型に適用する既定列 */
export const DEFAULT_INVOICE_EXCEL_COLUMN_KEYS: readonly (keyof typeof INVOICE_EXCEL_COLUMNS)[] = [
  "work_date",
  "order_number",
  "delivery_address",
  "cargo_name",
  "distance_km",
  "remarks",
];

export const InvoiceExcelColumnConfig = z.object({
  key: InvoiceExcelColumnKey,
  /** 取引先固有ラベル。省略時は INVOICE_EXCEL_COLUMNS の既定ラベル */
  label: z.string().min(1).optional(),
});

/** 明細部の型。detailed=配送1件=1行 / aggregated=カテゴリ集計 (注文一覧計・各特殊運賃計・その他) */
export const InvoiceExcelLayout = z.enum(["detailed", "aggregated"]);

/**
 * シート名の書式 (請求月から自動で組み立てる)。
 * - 'm_month': 「7月」 (既定) / 'mm_month': 「07月」
 * - 'yyyy_m_month': 「2026年7月」 / 'yyyy_mm_month': 「2026年07月」
 */
export const InvoiceExcelSheetNameFormat = z.enum([
  "m_month",
  "mm_month",
  "yyyy_m_month",
  "yyyy_mm_month",
]);

/**
 * 請求額ヘッダの型。
 * - 'simple': ご請求金額(税込) のみ
 * - 'carryover': 前回請求金額 / 当月請求金額 / 今回ご請求額(税込)
 * - 'carryover_balance': 前月請求金額 / 繰越し額 / 当月請求金額 / 今回ご請求額(税込)
 */
export const InvoiceExcelAmountHeader = z.enum(["simple", "carryover", "carryover_balance"]);

export const InvoiceExcelConfig = z.object({
  layout: InvoiceExcelLayout,
  /** 明細型の先頭列構成 (省略時は DEFAULT_INVOICE_EXCEL_COLUMN_KEYS)。集計型では無視 */
  columns: z.array(InvoiceExcelColumnConfig).min(1).optional(),
  /** 省略時 'simple' */
  amount_header: InvoiceExcelAmountHeader.optional(),
  /** 立寄・待機料(税抜) 列を出すか。省略時 true */
  stopover_waiting_column: z.boolean().optional(),
  /** 立寄・待機料列のラベル上書き (例: 「待機料」)。省略時「立寄・待機料」 */
  stopover_waiting_label: z.string().min(1).optional(),
  /** 集計型のカテゴリ行の下に出す注記 (例: 「★内訳は…参照ください」) */
  aggregated_note: z.string().min(1).optional(),
  /** 集計型に明細一覧シートを同梱する (ケータリング Ⓑ用明細相当)。省略時 false */
  detail_sheet: z.boolean().optional(),
  /** 備考欄の定型文。省略時「※お振込手数料は御社ご負担にてお願い致します」 */
  footer_note: z.string().min(1).optional(),
  /** シート名の書式。省略時 'm_month' (請求月の「M月」) */
  sheet_name_format: InvoiceExcelSheetNameFormat.optional(),
});

/** 一覧・Excel に渡す解決済み列定義 (label 解決後) */
export const ResolvedInvoiceExcelColumn = z.object({
  key: InvoiceExcelColumnKey,
  label: z.string(),
});

export const UpdateInvoiceExcelConfigInput = z.object({
  publicId: PublicIdSchema,
  config: InvoiceExcelConfig.nullable(),
});

export const GetInvoiceExcelConfigOutput = z.object({
  /** 保存されている生設定 (未設定は null = 既定様式) */
  config: InvoiceExcelConfig.nullable(),
  /** 明細型の解決済み列定義 (既定列セット込み) */
  columns: z.array(ResolvedInvoiceExcelColumn),
});

/**
 * 請求書明細行の様式用データ (invoice_items.excel_detail_json)。
 * 様式設定ありの詳細行と、F29に一意リンクする既定detailed課税行にbilling-generatorが付与する。
 * 金額内訳の不変条件: fare_amount + stopover_waiting_amount + expense_amount = item.amount。
 * 消費税は課税内訳 (fare + stopover_waiting) に対して計算する。
 * excel_detail が無い行 (手動追加等) は tax_rate>0 なら全額運賃列、0 なら全額立替列として扱う。
 */
export const InvoiceItemExcelDetail = z.object({
  work_date: z.iso.date().nullable().optional(),
  order_number: z.string().nullable().optional(),
  pickup_name: z.string().nullable().optional(),
  pickup_address: z.string().nullable().optional(),
  area: z.string().nullable().optional(),
  delivery_name: z.string().nullable().optional(),
  delivery_address: z.string().nullable().optional(),
  cargo_name: z.string().nullable().optional(),
  cargo_quantity: z.number().nullable().optional(),
  distance_km: z.number().nullable().optional(),
  /** 運賃 (税抜・課税) */
  fare_amount: z.number().int(),
  /** 立寄・待機料 (税抜・課税) */
  stopover_waiting_amount: z.number().int(),
  /** 高速・駐車場代等 (内税・消費税対象外) */
  expense_amount: z.number().int(),
});

export type InvoiceExcelColumnKeyDto = keyof typeof INVOICE_EXCEL_COLUMNS;
export type InvoiceExcelColumnConfigDto = z.infer<typeof InvoiceExcelColumnConfig>;
export type InvoiceExcelLayoutDto = z.infer<typeof InvoiceExcelLayout>;
export type InvoiceExcelAmountHeaderDto = z.infer<typeof InvoiceExcelAmountHeader>;
export type InvoiceExcelSheetNameFormatDto = z.infer<typeof InvoiceExcelSheetNameFormat>;
export type InvoiceExcelConfigDto = z.infer<typeof InvoiceExcelConfig>;
export type ResolvedInvoiceExcelColumnDto = z.infer<typeof ResolvedInvoiceExcelColumn>;
export type UpdateInvoiceExcelConfigInputDto = z.infer<typeof UpdateInvoiceExcelConfigInput>;
export type GetInvoiceExcelConfigOutputDto = z.infer<typeof GetInvoiceExcelConfigOutput>;
export type InvoiceItemExcelDetailDto = z.infer<typeof InvoiceItemExcelDetail>;
